import {z} from 'zod';

// -- Available Documents --

const BulkPrintDocumentKeySchema = z.enum([
  'order-list',
  'claim-handling-protocol',
  'claim-resolution-protocol',
  'offer',
  'job-card',
  'power-of-attorney',
  'tire-storage-form',
  'order-summary',
]);

export type BulkPrintDocumentKey = z.infer<typeof BulkPrintDocumentKeySchema>;

const AvailableDocumentStyleSchema = z.enum(['enable', 'disabled', 'hidden']);

const AvailableDocumentTemplateSchema = z.object({
  id: z.string(),
  title: z.string(),
  isDefault: z.boolean(),
  withAuthorization: z.boolean(),
});

export const GetServiceOrderAvailableDocumentsApiResponseSchema = z.array(
  z.object({
    key: BulkPrintDocumentKeySchema,
    style: AvailableDocumentStyleSchema,
    templates: z.array(AvailableDocumentTemplateSchema.nullable()).optional(),
  })
);

export type GetServiceOrderAvailableDocumentsApiResponse = z.infer<
  typeof GetServiceOrderAvailableDocumentsApiResponseSchema
>;

export const GetServiceOrderAvailableDocumentsApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderAvailableDocumentsApiArg = z.infer<
  typeof GetServiceOrderAvailableDocumentsApiArgSchema
>;

// -- Bulk Print Documents Restrictions --

const BulkPrintDocumentRestrictionSchema = z.object({
  message: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
});

const BulkPrintDocumentRestrictionsSchema = z
  .array(BulkPrintDocumentRestrictionSchema)
  .nullable()
  .optional();

export const GetServiceOrderBulkPrintDocumentsRestrictionsApiResponseSchema = z.array(
  z.object({
    documentKey: BulkPrintDocumentKeySchema,
    restriction: BulkPrintDocumentRestrictionsSchema,
  })
);

export type GetServiceOrderBulkPrintDocumentsRestrictionsApiResponse = z.infer<
  typeof GetServiceOrderBulkPrintDocumentsRestrictionsApiResponseSchema
>;

export const GetServiceOrderBulkPrintDocumentsRestrictionsApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
});

export type GetServiceOrderBulkPrintDocumentsRestrictionsApiArg = z.infer<
  typeof GetServiceOrderBulkPrintDocumentsRestrictionsApiArgSchema
>;

// -- Bulk Print Documents --

const BulkPrintDocumentRequestItemSchema = z.object({
  key: BulkPrintDocumentKeySchema,
  templateId: z.string().optional(),
});

const BulkPrintDocumentFileSchema = z.object({
  url: z.string().nullable(),
  pdfUrl: z.string().nullable(),
  fileId: z.string().nullable(),
  remoteFileId: z.string().nullable(),
});

export const PostServiceOrderBulkPrintDocumentsApiResponseSchema = z.array(
  z.object({
    documentKey: BulkPrintDocumentKeySchema,
    printout: BulkPrintDocumentFileSchema.nullable().optional(),
    restriction: BulkPrintDocumentRestrictionSchema.nullable().optional(),
  })
);

export type PostServiceOrderBulkPrintDocumentsApiResponse = z.infer<
  typeof PostServiceOrderBulkPrintDocumentsApiResponseSchema
>;

export const PostServiceOrderBulkPrintDocumentsApiArgSchema = z.object({
  serviceCaseId: z.string(),
  serviceOrderId: z.string(),
  body: z.array(BulkPrintDocumentRequestItemSchema),
});

export type PostServiceOrderBulkPrintDocumentsApiArg = z.infer<
  typeof PostServiceOrderBulkPrintDocumentsApiArgSchema
>;
